import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, DollarSign, Calendar, TrendingUp, MapPin, Sparkles, Building2 } from "lucide-react";

const PROVIDER_TINT = {
  Google: "bg-[#DBEAFE] text-[#1E40AF]",
  Apify: "bg-[#FEF3C7] text-[#B45309]",
  "Emergent AI": "bg-[#F3E8FF] text-[#6D28D9]",
  OpenRouteService: "bg-[#D1FAE5] text-[#047857]",
  Other: "bg-[#F3F4F6] text-[#4B5563]",
};

const usd = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function UsagePanel() {
  const [data, setData] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [scoring, setScoring] = useState(false);

  useEffect(() => {
    api.get("/usage").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const cleanAreas = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/clean-areas");
      toast.success(`Cleaned ${data.updated} area name${data.updated === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCleaning(false); }
  };
  const rescore = async () => {
    setScoring(true);
    try {
      const { data } = await api.post("/admin/backfill-scores");
      toast.success(`Scored ${data.scored} garage${data.scored === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setScoring(false); }
  };

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black text-[#1E3A8A]">Usage & cost</h2>
        <p className="text-sm text-[#6B7280]">What each connected service is costing you (estimated from published rates).</p>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="data-maintenance">
        <h3 className="text-sm font-bold uppercase text-[#6B7280] mb-1">Data maintenance</h3>
        <p className="text-xs text-[#9CA3AF] mb-3">Runs automatically, but you can re-run any time after importing or scraping.</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={cleanAreas} disabled={cleaning} data-testid="clean-areas-btn"
            className="h-11 px-4 rounded-lg border border-[#1E3A8A] text-[#1E3A8A] font-bold text-sm flex items-center gap-2 disabled:opacity-60">
            {cleaning ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} Re-clean area names
          </button>
          <button onClick={rescore} disabled={scoring} data-testid="rescore-btn"
            className="h-11 px-4 rounded-lg border border-[#6D28D9] text-[#6D28D9] font-bold text-sm flex items-center gap-2 disabled:opacity-60">
            {scoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Re-score unscored
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#1E3A8A] text-white rounded-lg p-5" data-testid="usage-total">
          <div className="text-xs font-bold uppercase tracking-wide text-white/70 flex items-center gap-1"><TrendingUp className="w-4 h-4" /> Total to date</div>
          <div className="text-4xl font-black mt-1">{usd(data.total_cost_usd)}</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-[#6B7280] flex items-center gap-1"><Calendar className="w-4 h-4" /> This month</div>
          <div className="text-4xl font-black mt-1 text-[#111827]">{usd(data.month_cost_usd)}</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-[#6B7280] flex items-center gap-1"><DollarSign className="w-4 h-4" /> Today</div>
          <div className="text-4xl font-black mt-1 text-[#111827]">{usd(data.today_cost_usd)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By provider */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <h3 className="text-sm font-bold uppercase text-[#6B7280] mb-3">By provider</h3>
          {data.by_provider.length === 0 ? <p className="text-sm text-[#9CA3AF]">No usage yet.</p> : (
            <div className="space-y-3">
              {data.by_provider.map((p) => (
                <div key={p.provider} className="flex items-center justify-between" data-testid={`provider-${p.provider}`}>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${PROVIDER_TINT[p.provider] || PROVIDER_TINT.Other}`}>{p.provider}</span>
                    <span className="text-sm text-[#6B7280]">{p.count.toLocaleString()} calls</span>
                  </div>
                  <span className="font-black text-[#111827]">{usd(p.cost_usd)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* By action */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <h3 className="text-sm font-bold uppercase text-[#6B7280] mb-3">By activity</h3>
          <table className="w-full" data-testid="usage-by-action">
            <thead>
              <tr className="text-xs font-bold uppercase text-[#9CA3AF] text-left">
                <th className="pb-2">Activity</th>
                <th className="pb-2 text-right">Count</th>
                <th className="pb-2 text-right">Rate</th>
                <th className="pb-2 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.by_action.length === 0 ? (
                <tr><td colSpan={4} className="py-4 text-center text-sm text-[#9CA3AF]">No usage yet.</td></tr>
              ) : data.by_action.map((a) => (
                <tr key={a.action} className="border-t border-[#F3F4F6]">
                  <td className="py-2 text-sm font-medium text-[#111827]">{a.label}</td>
                  <td className="py-2 text-sm text-right text-[#374151]">{a.count.toLocaleString()}</td>
                  <td className="py-2 text-xs text-right text-[#9CA3AF]">{a.unit_cost_usd === 0 ? "free" : `$${a.unit_cost_usd}/${a.unit}`}</td>
                  <td className="py-2 text-sm text-right font-black text-[#111827]">{usd(a.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.by_org && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="usage-by-org">
          <h3 className="text-sm font-bold uppercase text-[#6B7280] mb-3 flex items-center gap-1.5"><Building2 className="w-4 h-4" /> By organisation</h3>
          <div className="space-y-3">
            {data.by_org.map((o) => (
              <div key={o.organisation_id} className="flex items-center justify-between" data-testid={`org-usage-${o.organisation_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-bold text-[#111827] truncate">{o.organisation_name}</span>
                  <span className="text-sm text-[#6B7280] shrink-0">{o.count.toLocaleString()} calls</span>
                </div>
                <span className="font-black text-[#111827] shrink-0">{usd(o.cost_usd)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4">
        <h3 className="text-xs font-bold uppercase text-[#6B7280] mb-2">Rate card (estimates)</h3>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#6B7280]">
          {data.rate_card.map((r) => (
            <span key={r.action}>{r.label}: <b className="text-[#374151]">{r.usd === 0 ? "free" : `$${r.usd}/${r.unit}`}</b></span>
          ))}
        </div>
        <p className="text-xs text-[#9CA3AF] mt-2">Figures are estimated from each provider's published pricing and are for guidance; your actual invoices are the source of truth.</p>
      </div>
    </div>
  );
}
